import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  List,
  ListItem,
  Checkbox,
  Chip,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

const STORAGE_KEY = 'workspace_tasks_v1';

const PRIORITY_META = {
  High: { label: 'High', color: 'error' },
  Med: { label: 'Med', color: 'warning' },
  Low: { label: 'Low', color: 'default' },
};

function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [text, setText] = useState('');
  const [priority, setPriority] = useState('Med');
  const [filter, setFilter] = useState('All');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setTasks(parsed);
        }
      }
    } catch (e) {
      // ignore malformed storage
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
    } catch (e) {
      // ignore write failures (e.g. private mode)
    }
  }, [tasks, loaded]);

  const addTask = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const newTask = {
      id: `${Date.now()}` + Math.random(),
      text: trimmed,
      priority,
      done: false,
    };
    setTasks((prev) => [newTask, ...prev]);
    setText('');
    setPriority('Med');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      addTask();
    }
  };

  const toggleTask = (id) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t))
    );
  };

  const deleteTask = (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
  };

  const doneCount = tasks.filter((t) => t.done).length;
  const totalCount = tasks.length;

  const filteredTasks = tasks.filter((t) => {
    if (filter === 'Active') return !t.done;
    if (filter === 'Completed') return t.done;
    return true;
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Tasks
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Your personal to-do list. Add tasks, set a priority, and check them off as
        you go.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <TextField
            label="Add a task"
            placeholder="What needs to get done?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            size="small"
            fullWidth
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 } }}>
            <InputLabel id="task-priority-label">Priority</InputLabel>
            <Select
              labelId="task-priority-label"
              label="Priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Med">Med</MenuItem>
              <MenuItem value="High">High</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addTask}
            disabled={!text.trim()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add
          </Button>
        </Stack>
      </Paper>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <ToggleButtonGroup
          value={filter}
          exclusive
          size="small"
          onChange={(e, val) => {
            if (val !== null) setFilter(val);
          }}
        >
          <ToggleButton value="All">All</ToggleButton>
          <ToggleButton value="Active">Active</ToggleButton>
          <ToggleButton value="Completed">Completed</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          {doneCount} of {totalCount} done
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {filteredTasks.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <InboxOutlinedIcon
              sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.6 }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {totalCount === 0
                ? 'No tasks yet'
                : filter === 'Completed'
                ? 'Nothing completed yet'
                : filter === 'Active'
                ? 'All caught up!'
                : 'No tasks to show'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalCount === 0
                ? 'Add your first task above to get started.'
                : filter === 'Completed'
                ? 'Check off a task to see it here.'
                : filter === 'Active'
                ? 'You have no active tasks. Nice work!'
                : 'Try a different filter.'}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {filteredTasks.map((task, index) => {
              const meta = PRIORITY_META[task.priority] || PRIORITY_META.Med;
              return (
                <React.Fragment key={task.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem
                    sx={{ py: 1.25 }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete task"
                        onClick={() => deleteTask(task.id)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    }
                  >
                    <Checkbox
                      edge="start"
                      checked={task.done}
                      onChange={() => toggleTask(task.id)}
                      disableRipple
                      sx={{ mr: 1 }}
                    />
                    <Typography
                      sx={{
                        flex: 1,
                        mr: 1,
                        textDecoration: task.done ? 'line-through' : 'none',
                        color: task.done ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {task.text}
                    </Typography>
                    <Chip
                      label={meta.label}
                      color={meta.color}
                      size="small"
                      variant={meta.color === 'default' ? 'outlined' : 'filled'}
                      sx={{ mr: 5, opacity: task.done ? 0.6 : 1 }}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>
    </Box>
  );
}

export default TasksPage;
